import { Component, OnInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef, Injector, Input, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { HttpHelpService } from '../../../Data/service/http-help.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Customer } from '../../models/customer.model';
import { BaseControlComponent } from '../base-control/base-control.component';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Dropdown } from 'primeng/dropdown';

/**
 * Component for selecting a customer from a dropdown list.
 * Extends BaseControlComponent for common form control functionality.
 */
@Component({
	selector: 'app-customer-select',
	templateUrl: './customer-select.component.html',
	styleUrls: ['./customer-select.component.scss']
})
export class CustomerSelectComponent extends BaseControlComponent implements OnInit, OnDestroy, OnChanges {

	/**
	 * Emits the selected customer ID when the selection changes.
	 * @type {EventEmitter<number | null>}
	 */
	@Output() customerChange = new EventEmitter<number | null>();

	/**
	 * Company ID to filter customers by. If provided, will use this instead of localStorage.
	 * @type {number | null}
	 */
	@Input() companyId: number | null = null;

	/**
	 * List of customers to display in the dropdown.
	 * @type {Customer[]}
	 */
	customersList: Customer[] = [];
	/**
	 * The current language setting for localization.
	 * @type {string}
	 */
	curentLang: string = localStorage.getItem('curentLang') || 'en';
	/**
	 * Flag indicating if customer data is currently being loaded.
	 * @type {boolean}
	 */
	isLoading = false;
	/**
	 * Flag to control the visibility of the add customer modal.
	 * @type {boolean}
	 */
	showAddCustomerModal: boolean = false;

	/** Flag to enable debug logging, passed down to child components like the modal. */
	@Input() debug: boolean = false;

	// Add state for popup
	isMobileDropdownOpen = false;

	@ViewChild('mobileDropdown') mobileDropdownRef!: Dropdown;

	searchTerm: string = '';

	private maximizedOnce = false;

	get filteredCustomers(): Customer[] {
		const term = this.searchTerm.trim().toLowerCase();
		if (!term) return this.customersList;
		return this.customersList.filter(c =>
			(this.getCustomerName(c) || '').toLowerCase().includes(term) ||
			(c.id_number || '').toLowerCase().includes(term) ||
			(c.customer_vat_number || '').toLowerCase().includes(term) ||
			(c.customer_phone || '').toLowerCase().includes(term)
		);
	}

	selectCustomer(customer: Customer): void {
		this._value = customer.customer_id;
		this.onCustomerSelectionChange(customer.customer_id);
		this.closeMobileDropdown();
	}

	/**
	 * Injects required services and passes dependencies to the base class.
	 * @param {HttpHelpService} service - Service for HTTP requests.
	 * @param {NgxSpinnerService} spinner - Service for displaying loading spinners.
	 * @param {MessageService} messageService - Service for displaying messages (toasts).
	 * @param {TranslateService} translate - Service for handling translations.
	 * @param {ChangeDetectorRef} cdr - Service for triggering change detection.
	 * @param {Injector} injector - Injector for dependency injection.
	 */
	constructor(
		private service: HttpHelpService,
		private spinner: NgxSpinnerService,
		protected override injector: Injector,
		protected override messageService: MessageService,
		protected override translate: TranslateService,
		protected override cdr: ChangeDetectorRef
	) {
		super(injector, translate, cdr, messageService);
	}

	/**
	 * @description Angular lifecycle hook called after every check of the component's view.
	 * Used here to maximize the dialog automatically on small screens when it first opens.
	 */
	ngAfterViewChecked(): void {
		if (
			this.isMobileDropdownOpen &&
			window.innerWidth < 768 &&
			!this.maximizedOnce
		) {
			this.clickMaximizeButton();
			this.maximizedOnce = true;
		}
	}

	/**
	 * @protected
	 * @description Programmatically clicks the maximize button of the PrimeNG dialog.
	 * Used for responsive behavior on small screens.
	 */
	protected clickMaximizeButton() {
		// Note: This relies on PrimeNG's v13 internal CSS classes and might break with updates.
		const dialogEl = document.querySelector('.p-dialog') as HTMLElement;
		if (dialogEl) {
			const maximizeBtn = dialogEl.querySelector('.p-dialog-header-maximize') as HTMLElement;
			if (maximizeBtn) {
				maximizeBtn.click();
			}
		}
	}

	/**
	 * Initializes the component, loads customers, and subscribes to parent form changes.
	 * @override
	 */
	override ngOnInit(): void {
		super.ngOnInit();

		if (this.control) {
			this.getCustomers();
			this.parentValueSub = this.control.valueChanges.subscribe(value => {
});
		}
	}

	/**
	 * Handles changes to input properties, particularly companyId changes.
	 * Reloads customers when company ID changes.
	 * @param changes The changed input properties
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (changes['companyId']) {
			const previousCompanyId = changes['companyId'].previousValue;
			const currentCompanyId = changes['companyId'].currentValue;
			const isFirstChange = changes['companyId'].firstChange;
			
			// Only reload if it's not the first change (to avoid reloading on initial load)
			if (!isFirstChange) {
// Reset customer selection when company changes
				if (this.control) {
					this.control.setValue(null, { emitEvent: false });
				}
				this._value = null;
				
				// Reload customers for the new company
				if (currentCompanyId !== null && currentCompanyId !== undefined) {
					this.getCustomers();
				} else {
					// Clear customers list if no company selected
					this.customersList = [];
				}
			}
		}
	}

	/**
	 * Loads the list of active customers from the backend service based on company ID.
	 * Handles loading state and error notifications.
	 * Sets the first customer as default if the control is empty.
	 */
	getCustomers(): void {
		if (this.isLoading) return;
		
		// Get company ID from input first, then fallback to localStorage
		let companyId: number | null = null;
		
		if (this.companyId !== null && this.companyId !== undefined) {
			companyId = this.companyId;
} else {
			// Fallback to localStorage (default company selected in header)
			const storedCompanyId = localStorage.getItem('companyDefault');
			if (storedCompanyId) {
				companyId = Number(storedCompanyId);
}
		}
		
		if (!companyId) {
			if (this.debug) {
				console.warn('[DEBUG/CustomerSelect] No company ID found');
			}
			this.messageService.add({
				severity: 'warn',
				summary: this.translate.instant('shared.customerSelect.loadErrorSummary'),
				detail: 'Please select a company first'
			});
			return;
		}

		this.isLoading = true;
		this.spinner.show();
		this.service.getCustomersByCompanyId(companyId).subscribe({
			next: (res: any) => {
				// Service already normalises response into a plain array.
				this.customersList = Array.isArray(res) ? res : [];
// let valueToSet = this.control?.value;
				// if (
				// 	this.customersList.length > 0 &&
				// 	(valueToSet === null || valueToSet === undefined || valueToSet === '')
				// ) {
				// 	valueToSet = this.customersList[0].customer_id;
				// 	this.control?.setValue(valueToSet, { emitEvent: false });
				// }
				setTimeout(() => {
					// this.writeValue(valueToSet);
					this.cdr.detectChanges();
				});
				this.isLoading = false;
				this.spinner.hide();
			},
			error: (error) => {
				this.messageService.add({
					severity: 'error',
					summary: this.translate.instant('shared.customerSelect.loadErrorSummary'),
					detail: this.translate.instant('shared.customerSelect.loadErrorDetail')
				});
				this.isLoading = false;
				this.spinner.hide();
			}
		});
	}

	/**
	 * Handles selection changes in the dropdown.
	 * Emits the selected customer ID and marks the control as interacted.
	 * @param {number | null} selectedId - The ID of the selected customer, or null if cleared.
	 */
	onCustomerSelectionChange(selectedId: number | null): void {
this.propagateChange(selectedId);
		this.customerChange.emit(selectedId);
		this.markAsInteracted();
	}

	/**
	 * Determines the property name for the customer name based on the current language.
	 * Used for `optionLabel` and `filterBy` in the p-dropdown.
	 * @returns {string} The property name ('customer_name_ar', 'customer_name_he', or 'customer_name_en').
	 */
	getCustomerNameProperty(): string {
		switch (this.curentLang) {
			case 'ar': return 'customer_name_ar';
			case 'he': return 'customer_name_he';
			default: return 'customer_name_en';
		}
	}

	/**
	 * Retrieves the customer name in the appropriate language for display.
	 * Provides fallbacks if the primary language name is missing.
	 * @param {Customer | null} item - The customer object.
	 * @returns {string} The localized customer name, or an empty string if item is null.
	 */
	getCustomerName(item: Customer | null): string {
		if (!item) return '';
		switch (this.curentLang) {
			case 'ar': return item.customer_name_ar || item.customer_name_he || item.customer_name_en || '';
			case 'he': return item.customer_name_he || item.customer_name_en || '';
			default: return item.customer_name_en || item.customer_name_he || '';
		}
	}

	/**
	 * Opens the modal dialog for adding a new customer.
	 */
	openAddCustomerModal(): void {
		this.showAddCustomerModal = true;
	}

	/**
	 * Handles the save event emitted from the add customer modal.
	 * Adds the new customer to the dropdown list, selects it in the form,
	 * emits the change, and closes the modal.
	 * Uses setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError.
	 * @param {Customer} newCustomer - The newly created customer object.
	 */
	handleCustomerModalSave(newCustomer: Customer): void {
		if (newCustomer && newCustomer.customer_id) {
this.customersList = [...this.customersList, newCustomer];
			this.showAddCustomerModal = false;

			setTimeout(() => {
				this.control?.setValue(newCustomer.customer_id);
				this.customerChange.emit(newCustomer.customer_id);
				this.cdr.detectChanges();
			});

		} else {
			this.showAddCustomerModal = false;
		}
	}

	/**
	 * Handles the close event emitted from the add customer modal.
	 * Simply closes the modal.
	 */
	handleCustomerModalClose(): void {
		this.showAddCustomerModal = false;
	}

	openMobileDropdown(): void {
		this.isMobileDropdownOpen = true;
		setTimeout(() => {
			this.mobileDropdownRef?.show?.();
		}, 0);
		this.maximizedOnce = false;
	}

	closeMobileDropdown(): void {
		this.isMobileDropdownOpen = false;
	}

	isMobile(): boolean {
		return window.innerWidth <= 600 || /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
	}

	getMobileDialogStyle() {
		return this.isMobile()
			? { width: '100vw', height: '100vh', maxWidth: '100vw', maxHeight: '100vh', top: '0', left: '0', margin: '0', padding: '0' }
			: { width: '90vw', maxWidth: '400px' };
	}

	getMobileDialogContentStyle() {
		return this.isMobile()
			? { height: '100%', padding: '1.5rem', display: 'flex', 'flex-direction': 'column', 'justify-content': 'center' }
			: { padding: '1.5rem' };
	}

	getSelectedCustomerName(): string {
		const selected = this.customersList.find(c => c.customer_id === this._value);
		return selected ? this.getCustomerName(selected) : '';
	}
}